import { FormattedMessage } from 'react-intl'
import { useMutation, useApolloClient, gql } from '@apollo/client'
import styled from 'styled-components'

import { WarningIcon } from '@10xLabs/react-web/components/Icons/Warning'
import { Dropdown } from 'components/v2/Dropdown'
import { DotsIcon } from 'components/Icons/DotsIcon'
import List from 'components/v2/List'
import { resolvers as sitesResolvers } from 'resolvers/sites'
import { ReactElement } from 'react'
import { GTSite } from 'gql/types.generated'
import IconButton from 'components/v2/IconButton'

const DeactivateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
`

const ACTIVATE_SITE = gql`
  mutation ActivateSite($siteId: ID!) {
    activateSite(input: { id: $siteId }) {
      id
      isActive
    }
  }
`

const ACTIVATE_LOCAL_SITE = gql`
  mutation ActivateLocalSite($siteId: ID!) {
    activateSite(siteId: $siteId) @client
  }
`

const DEACTIVATE_SITE = gql`
  mutation DeactivateSite($siteId: ID!) {
    deactivateSite(input: { id: $siteId }) {
      id
      isActive
    }
  }
`

const DEACTIVATE_LOCAL_SITE = gql`
  mutation DeactivateSite($siteId: ID!) {
    deactivateSite(siteId: $siteId) @client
  }
`

export const SitesActions = ({ site }: { site: GTSite }): ReactElement => {
  const client = useApolloClient()
  client.addResolvers(sitesResolvers)

  const [activateLocalSite] = useMutation(ACTIVATE_LOCAL_SITE)
  const [activateSite, { loading: activateLoading, error: activateError }] = useMutation(ACTIVATE_SITE, {
    onCompleted() {
      activateLocalSite({
        variables: {
          siteId: site.id
        }
      })
    }
  })
  const [deactivateLocalSite] = useMutation(DEACTIVATE_LOCAL_SITE)
  const [deactivateSite, { loading: deactivateLoading, error: deactivateError }] = useMutation(DEACTIVATE_SITE, {
    onCompleted() {
      deactivateLocalSite({
        variables: {
          siteId: site.id
        }
      })
    }
  })

  const toggleSiteActivation = (): void => {
    if (site.isActive) {
      deactivateSite({
        variables: {
          siteId: site.id
        }
      })
    } else {
      activateSite({
        variables: {
          siteId: site.id
        }
      })
    }
  }

  return (
    <Dropdown toggle={<IconButton icon={<DotsIcon />} noBorder smallPadding />} noPadding noIndicator left>
      <List.Container>
        <List.Item noPadding>
          <List.Link to={`/sites/${site.id}`}>
            <FormattedMessage id="sites.list.actions.edit" defaultMessage="Edit site" />
          </List.Link>
        </List.Item>
        <List.Item action onClick={toggleSiteActivation} loading={activateLoading || deactivateLoading}>
          <DeactivateWrapper>
            {site.isActive ? (
              <FormattedMessage id="dictionary.deactivate" defaultMessage="Deactivate" />
            ) : (
              <FormattedMessage id="dictionary.activate" defaultMessage="Activate" />
            )}
            {(deactivateError || activateError) && <StyledWarningIcon />}
          </DeactivateWrapper>
        </List.Item>
      </List.Container>
    </Dropdown>
  )
}
